import React from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import SaveRounded from '@mui/icons-material/SaveRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import { EmptyState, Panel, SelectMenu, SettingRow, ToolbarButton } from '../../../ui-kit';
import { useCan } from '../../../ui/PermissionsProvider';
import {
  fetchWaConfiguration, saveWaSettings, type WaConfiguration, type WaSettings,
} from '../../../services/websiteAssistantService';
import { useLive } from '../rocco/live';

type Draft = WaSettings;

/** "Chosen for you" when a setting is empty and the next lead picks it. */
const AUTO = '';

/**
 * How the Website Assistant is set up: what it says and how it looks, where
 * its leads go in AgencyZoom, and where it finds a client's policy. AgencyZoom's
 * pipelines, lead sources and team are read from the connector, so a choice
 * is always one AgencyZoom knows.
 */
export default function ConfigurationPage() {
  const { data, error, loading, reload } = useLive<WaConfiguration>(fetchWaConfiguration, 60_000);
  const canEdit = useCan('manage_workspace');
  const [draft, setDraft] = React.useState<Draft | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [problem, setProblem] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (data && !draft) setDraft(data.settings);
  }, [data, draft]);

  if (error && !data) return <EmptyState message={error} />;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setSaved(false);
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  };
  const changed = Boolean(data && draft && (Object.keys(draft) as (keyof Draft)[]).some((k) => draft[k] !== data.settings[k]));
  const isColour = (v: string) => /^#[0-9a-f]{6}$/i.test(v);
  const colourOk = !draft || (isColour(draft.brand_color) && (!draft.accent_color || isColour(draft.accent_color)));

  const save = async () => {
    if (!draft || !data) return;
    setSaving(true);
    setProblem(null);
    try {
      const change = Object.fromEntries((Object.keys(draft) as (keyof Draft)[])
        .filter((k) => draft[k] !== data.settings[k] && k !== 'policy_connector_id')
        .map((k) => [k, draft[k]]));
      await saveWaSettings(change);
      await reload();
      setSaved(true);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Could not save the settings');
    } finally {
      setSaving(false);
    }
  };

  const text = (id: 'agency_name' | 'greeting' | 'notice' | 'brand_color' | 'accent_color' | 'font' | 'lead_tags', label: string,
    description: string, multiline = false) => (
    <SettingRow label={label} htmlFor={`wa-${id}`} description={description}
      error={(id === 'brand_color' && draft && !isColour(draft.brand_color))
        || (id === 'accent_color' && draft?.accent_color && !isColour(draft.accent_color)) ? 'Write it like #1f3a5f' : null}>
      <TextField id={`wa-${id}`} size="small" value={draft?.[id] ?? ''} disabled={!canEdit}
        onChange={(e) => set(id, e.target.value)} multiline={multiline} minRows={multiline ? 2 : undefined}
        sx={{ width: { xs: '100%', md: multiline ? 420 : 280 } }} />
    </SettingRow>
  );

  const az = data?.agencyzoom;
  const pipeline = az?.pipelines.find((p) => p.id === draft?.pipeline_id) ?? az?.pipelines[0];
  const choose = (id: 'pipeline_id' | 'stage_id' | 'lead_source_id' | 'assign_to', label: string, description: string,
    options: { id: number; name: string }[]) => (
    <SettingRow label={label} description={description}>
      <SelectMenu label={label} value={draft?.[id] ? String(draft[id]) : AUTO} disabled={!canEdit || !options.length}
        options={[{ value: AUTO, label: 'Chosen for me' }, ...options.map((o) => ({ value: String(o.id), label: o.name }))]}
        onChange={(v) => set(id, v ? Number(v) : null)} />
    </SettingRow>
  );

  const goes = data?.leads_go_to;
  return (
    <Stack spacing={2}>
      <Panel title="The chat" subtitle="What visitors see on your website"
        loading={loading || !draft} loadingHeight={300}
        actions={(
          <Stack direction="row" spacing={1}>
            <ToolbarButton variant="outlined" icon={<OpenInNewRounded />} disabled={!data}
              onClick={() => data && window.open(data.widget_path, '_blank', 'noopener')}>
              Open the chat
            </ToolbarButton>
            {canEdit ? (
              <ToolbarButton variant="brand" icon={<SaveRounded />} onClick={save} loading={saving}
                disabled={!changed || !colourOk}>
                Save
              </ToolbarButton>
            ) : null}
          </Stack>
        )}>
        {draft ? (
          <Stack spacing={2}>
            {text('agency_name', 'Agency name', 'The chat speaks for the agency: its name is in the header and in what it says.')}
            {text('greeting', 'First message', 'What the chat says when a visitor opens it.', true)}
            {text('notice', 'Notice under the header', 'A line every visitor sees, when compliance asks for one. Leave empty for none.', true)}
            {text('accent_color', 'Main colour', "The agency's own colour, for the chat's header, its buttons and the visitor's messages.")}
            {text('brand_color', 'Heading colour', "The website's headings, for the demo website's headings and banner. The main colour is this one when left empty.")}
            {text('font', 'Typeface', "The website's typeface, as Google Fonts names it, like Jost. Leave empty for ours.")}
          </Stack>
        ) : null}
      </Panel>

      <Panel title="Where leads go" subtitle={goes
        ? `The next lead goes to ${goes.pipeline} · ${goes.stage}, from ${goes.lead_source}, for ${goes.assign_to}`
        : data?.agencyzoom_problem || ' '}
        loading={loading || !draft} loadingHeight={260}>
        {data?.agencyzoom_problem ? (
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.semantic.danger })}>{data.agencyzoom_problem}</Typography>
        ) : draft && az ? (
          <Stack spacing={2}>
            {choose('pipeline_id', 'Pipeline', 'The AgencyZoom lead pipeline new leads go into.', az.pipelines)}
            {choose('stage_id', 'Stage', 'Where in that pipeline they start.', pipeline?.stages ?? [])}
            {choose('lead_source_id', 'Lead source', 'What AgencyZoom records the lead as coming from. Add a "Website" source in AgencyZoom and it is picked for you.', az.sources)}
            {choose('assign_to', 'Assigned to', 'Who each lead goes to, and who gets a call-back task when a question needs a person.', az.team)}
            {text('lead_tags', 'Tags', 'Tags on every lead, separated by semicolons.')}
          </Stack>
        ) : null}
      </Panel>

      <Panel title="Client questions" subtitle={data?.policy_system
        ? `A client gives their policy number and ZIP code, and is answered from ${data.policy_system}`
        : 'Connect a policy system to answer clients about their own policy'}>
        <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
          Only what the policy's own records say is answered, every amount as the records hold it. Whether something
          is covered, or anything the records do not say, is passed to the person above as a call-back task.
        </Typography>
      </Panel>

      {problem ? (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.danger })}>{problem}</Typography>
      ) : saved ? (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.semantic.success })}>Saved. The next chat uses these settings.</Typography>
      ) : null}
    </Stack>
  );
}
